import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

const AddForm = ({ onClose, onSubmit }) => {

  const [modulesData, setModulesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedSubModules, setSelectedSubModules] = useState([]);

  const [formData, setFormData] = useState({
    packageName: "",
    packageDetails: "",
    packageDuration: "",
    modules: []
  });

  const Auth = localStorage.getItem("authToken");

  useEffect(() => {
    fetchModulesData();
  }, []);

  const fetchModulesData = async () => {
    try {

      const response = await fetch(
        "https://support.ace-school-backend.serverscripts.in/api/v1/admin/modules/view",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Auth}`,
          },
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error("Failed to fetch modules");
      }

      setModulesData(result.data || []);

    } catch (err) {

      setError(err.message);

    } finally {

      setLoading(false);
    }
  };

  const handleChange = (e) => {

    const { name, value } = e.target;
    

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddModule = () => {

    if (!selectedModule) {
      toast.error("Please select a module");
      return;
    }

    if (selectedSubModules.length === 0) {
      toast.error("Please select at least one submodule");
      return;
    }

    const newModules = selectedModule.sub_modules
      .filter((sub) => selectedSubModules.includes(sub.id))
      .map((subModule) => ({
        moduleId: selectedModule.id,
        subModuleId: subModule.id,
        moduleName: selectedModule.moduleName,
        subModuleName: subModule.subModuleName
      }));

    setFormData((prev) => ({
      ...prev,
      modules: [...prev.modules, ...newModules]
    }));

    setModulesData((prev) =>
      prev.filter((module) => module.id !== selectedModule.id)
    );

    setSelectedModuleId("");
    setSelectedModule(null);
    setSelectedSubModules([]);

    toast.success("Module added successfully");
  };

  const handleRemoveModule = (moduleId) => {

    const removedModuleItems = formData.modules.filter(
      (item) => item.moduleId === moduleId
    );

    const remainingModules = formData.modules.filter(
      (item) => item.moduleId !== moduleId
    );

    setFormData((prev) => ({
      ...prev,
      modules: remainingModules
    }));

    if (removedModuleItems.length > 0) {

      const restoredModule = {
        id: removedModuleItems[0].moduleId,
        moduleName: removedModuleItems[0].moduleName,
        sub_modules: removedModuleItems.map((item) => ({
          id: item.subModuleId,
          subModuleName: item.subModuleName
        }))
      };

      setModulesData((prev) => [...prev, restoredModule]);
    }
  };

  const groupedModules = Object.values(
    formData.modules.reduce((acc, item) => {

      if (!acc[item.moduleId]) {
        acc[item.moduleId] = {
          moduleId: item.moduleId,
          moduleName: item.moduleName,
          subModules: []
        };
      }

      acc[item.moduleId].subModules.push(item.subModuleName);

      return acc;

    }, {})
  );

  const handleSubmit = () => {

    if (!formData.packageName || !formData.packageDuration) {
      toast.error("Package Name and Package Duration are required");
      return;
    }

    if (formData.modules.length === 0) {
      toast.error("Please add at least one module");
      return;
    }

    if (onSubmit) {
      console.log("Submitting form data:", formData);
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">

      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl p-6">

        {/* HEADER */}
        <div className="flex justify-between items-center border-b pb-3 mb-4">

          <h2 className="text-xl font-semibold text-gray-800">
            Add Package
          </h2>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-lg"
          >
            ✕
          </button>

        </div>

        <div className="space-y-4 max-h-[75vh] overflow-y-auto">

          {/* PACKAGE NAME */}
          <div>

            <label className="block font-semibold text-gray-700">
              Package Name *
            </label>

            <input
              type="text"
              name="packageName"
              value={formData.packageName}
              onChange={handleChange}
              placeholder="Enter package name"
              className="w-full border rounded-md px-3 py-2 mt-1"
            />

          </div>

          {/* PACKAGE DETAILS */}
          <div>

            <label className="block font-semibold text-gray-700">
              Package Details *
            </label>

            <input
              type="text"
              name="packageDetails"
              value={formData.packageDetails}
              onChange={handleChange}
              placeholder="Enter package details"
              className="w-full border rounded-md px-3 py-2 mt-1"
            />

          </div>
          <div>

            <label className="block font-semibold text-gray-700">
              Package Duration *
            </label>

            <input
              type="text"
              name="packageDuration"
              value={formData.packageDuration}
              onChange={handleChange}
              placeholder="Enter package duration"
              className="w-full border rounded-md px-3 py-2 mt-1"
            />

          </div>

          {/* SELECT MODULE */}
          <div>

            <label className="block font-semibold text-gray-700 mb-2">
              Select Module
            </label>

            <div className="flex gap-3">

              <select
                value={selectedModuleId}
                onChange={(e) => {

                  const moduleId = Number(e.target.value);

                  setSelectedModuleId(moduleId);

                  const module = modulesData.find(
                    (item) => item.id === moduleId
                  );

                  setSelectedModule(module);

                  // ALL SUBMODULES SELECTED BY DEFAULT
                  setSelectedSubModules(
                    module?.sub_modules?.map((sub) => sub.id) || []
                  );

                }}
                className="w-full border rounded-md px-3 py-2"
              >

                <option value="">Choose Module</option>

                {modulesData.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.moduleName}
                  </option>
                ))}

              </select>

              <button
                type="button"
                onClick={handleAddModule}
                className="bg-blue-600 text-white px-4 rounded-md hover:bg-blue-700"
              >
                Add
              </button>

            </div>

          </div>

          {/* SUBMODULES */}
          {
            selectedModule && (
              <div className="border rounded-lg p-4">

                <h3 className="font-semibold text-blue-600 mb-3">
                  {selectedModule.moduleName} Sub Modules
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">

                  {selectedModule.sub_modules?.map((subModule) => (

                    <label
                      key={subModule.id}
                      className="flex items-center gap-2 border rounded-md p-2 cursor-pointer hover:bg-gray-50"
                    >

                      <input
                        type="checkbox"
                        checked={selectedSubModules.includes(subModule.id)}

                        onChange={(e) => {

                          if (e.target.checked) {

                            setSelectedSubModules((prev) => [
                              ...prev,
                              subModule.id
                            ]);

                          } else {

                            setSelectedSubModules((prev) =>
                              prev.filter((id) => id !== subModule.id)
                            );
                          }
                        }}
                      />

                      <span>{subModule.subModuleName}</span>

                    </label>

                  ))}

                </div>

              </div>
            )
          }

          {/* ADDED MODULES */}
          <div className="space-y-4">

            {groupedModules.map((module) => (

              <div
                key={module.moduleId}
                className="border rounded-lg p-4 bg-gray-50"
              >

                <div className="flex justify-between items-center mb-3">

                  <h3 className="font-bold text-blue-600">
                    {module.moduleName}
                  </h3>

                  <button
                    type="button"
                    onClick={() => handleRemoveModule(module.moduleId)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>

                </div>

                <div className="flex flex-wrap gap-2">

                  {module.subModules.map((sub, index) => (

                    <span
                      key={index}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                    >
                      {sub}
                    </span>

                  ))}

                </div>

              </div>

            ))}

          </div>

        </div>

        {/* FOOTER */}
        <div className="mt-6 flex justify-end gap-3">

          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Submit
          </button>

        </div>

      </div>

    </div>
  );
};

export default AddForm;